import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../auth/AuthContext'
import { useToast } from '../ui/ToastProvider'
import { Button } from '../components/ui/button'

/** 공식: 질병분류정보센터 KOICD — https://www.koicd.kr/ */
const KCD_BROWSE_URL = 'https://www.koicd.kr/'

const TAB_KEYS = {
  CODE: 'code',
  DISEASE_RIDER: 'disease-rider',
  RIDER_DISEASE: 'rider-disease',
}

/**
 * 사내 KCD_MAJOR_SETS.json의 major11 구간을 대표하는 코드 예시(정확한 보장·문진 분류는 증권·운영 기준).
 * 커넥션랩스 예시 세트와 겹치지 않게 선별.
 */
const MAJOR11_CHIPS = [
  { code: 'C18', name: '대장 악성신생물' },
  { code: 'I10', name: '원발성 고혈압' },
  { code: 'I21', name: '급성 심근경색' },
  { code: 'I35', name: '대동맥판 질환' },
  { code: 'I63', name: '뇌경색' },
  { code: 'K74', name: '간 경변' },
  { code: 'E11', name: '제2형 당뇨병' },
  { code: 'B20', name: 'HIV 질병' },
  { code: 'Z21', name: '무증상 HIV 감염' },
  { code: 'K64', name: '치핵·치질' },
  { code: 'K62', name: '항문·직장 기타 질환' },
]

/** 통원·상담에서 자주 조회하는 질환(일상 진료 중심). */
const FREQUENT_CHIPS = [
  { code: 'J069', name: '급성 상기도 감염' },
  { code: 'M545', name: '요통' },
  { code: 'K297', name: '위염' },
  { code: 'H109', name: '결막염' },
  { code: 'N390', name: '요로 감염' },
  { code: 'R51', name: '두통' },
  { code: 'L209', name: '아토피 피부염' },
  { code: 'J209', name: '급성 기관지염' },
  { code: 'B02', name: '대상포진' },
  { code: 'K35', name: '급성 충수염' },
]

/** KCD 대분류(앞 글자) 기준 참고 힌트 — 상품·약관·특약명은 회사마다 다릅니다. */
const CHAPTER_RIDER_GUIDE = {
  A: { block: 'A00–B99', label: '감염·기생충', hints: ['질병·상해 입원/통원', '감염성 질환 일당·수술 담보(상품별)'] },
  C: { block: 'C00–D48', label: '신생물', hints: ['암(진단·수술·치료) 관련 담보', '암특약·뇌·심장 등 전이/재진단(약관 확인)'] },
  D: { block: 'D50–D89', label: '혈액·면역', hints: ['혈액·비암 신생물·기타(상품별)', '기존·보장범위·면책 여부 확인'] },
  E: { block: 'E00–E90', label: '내분비·대사', hints: ['당뇨·갑상선 등 만성질환', '질병특정·고정지급형(상품별)'] },
  I: { block: 'I00–I99', label: '순환계', hints: ['심장·뇌혈관(뇌졸중·심근경색 등)', '질병/상해 담보·인수심사 기준'] },
  J: { block: 'J00–J99', label: '호흡기', hints: ['폐·기관지·감염', '질병·일당·수술(상품별)'] },
  K: { block: 'K00–K93', label: '소화기', hints: ['위·장·담·간·췌장', '특정 수술·질병(상품별)'] },
  M: { block: 'M00–M99', label: '근골격', hints: ['관절·척추·골절(질병/상해)', '수술·일당·장해(상품별)'] },
  N: { block: 'N00–N99', label: '비뇨기', hints: ['신장·요로·전립선', '질병/수술(상품별)'] },
  O: { block: 'O00–O99', label: '임신·출산', hints: ['임신·분만·산후 관련', '출산급여·산모 특약(상품별)'] },
  S: { block: 'S00–T98', label: '손상·중독', hints: ['골절·염좌·개방성 창·화상', '상해 일당·수술·후유장해(상품별)'] },
  T: { block: 'S00–T98', label: '손상·중독', hints: ['골절·화상·외인 후유증 등', '상해 관련 일당·수술·후유장해(상품별)'] },
}

const RIDER_TO_DISEASE_ROWS = [
  {
    category: '암·신생물 계열',
    riders: '암진단·암수술·암치료·항암방사선·항암약물 등',
    kcd: 'C00–D48 구간이 대표적',
    examples: 'C50, C34, C18, C73 등',
  },
  {
    category: '심장·혈관',
    riders: '급성심근경색·협심증·뇌졸중·뇌혈관진단 등',
    kcd: 'I20–I25, I60–I69 등',
    examples: 'I21, I63, I61 등',
  },
  {
    category: '수술·입원 일반',
    riders: '질병·상해 입원일당·수술비·집중치료실 등',
    kcd: '전 장(KCD) — 사고·질병코드 모두',
    examples: '질병 K·상해 S/T 등',
  },
  {
    category: '골절·상해',
    riders: '골절·화상·후유장해(상해)',
    kcd: 'S00–T98 등 손상 코드',
    examples: 'S72, T 코드 등',
  },
]

function chapterKeyFromCode(code) {
  const c = String(code || '').toUpperCase().replace(/\./g, '').trim()
  if (!c) return ''
  const letter = c.charAt(0)
  return /[A-Z]/.test(letter) ? letter : ''
}

function guessSearchMode(q) {
  const t = String(q || '').trim()
  if (!t) return 'name'
  if (/^[A-Z]\d[\d.]*/i.test(t)) return 'code'
  return 'name'
}

export default function WorkSupportDiseaseCodeTool() {
  const { show } = useToast()
  const { hasAnyRole, Roles } = useAuth()
  const isOperator = hasAnyRole([Roles.OPERATOR])

  const [tab, setTab] = useState(TAB_KEYS.CODE)
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [meta, setMeta] = useState(null)
  const [selected, setSelected] = useState(null)
  const [riderQueryCode, setRiderQueryCode] = useState('')

  const guideForSelected = useMemo(() => {
    const code = selected?.code || riderQueryCode
    const key = chapterKeyFromCode(code)
    return key ? CHAPTER_RIDER_GUIDE[key] : null
  }, [selected, riderQueryCode])

  const runLocalSearch = useCallback(
    async (raw) => {
      const q = String(raw ?? query).trim()
      if (!q) {
        setItems([])
        setMeta(null)
        return
      }
      setLoading(true)
      try {
        const mode = guessSearchMode(q)
        const res = await api.get('/kcd/local-search', {
          params: { q, mode, size: 40 },
        })
        const data = res?.data?.data
        const list = Array.isArray(data?.items) ? data.items : []
        setItems(list)
        setMeta(data?.meta || null)
      } catch (e) {
        setItems([])
        setMeta(null)
        const msg = e?.response?.data?.message || '검색에 실패했습니다.'
        show(msg, 'error')
      } finally {
        setLoading(false)
      }
    },
    [query, show],
  )

  useEffect(() => {
    if (tab !== TAB_KEYS.CODE) return undefined
    const t = String(query).trim()
    if (t.length < 2) {
      setItems([])
      setMeta(null)
      return undefined
    }
    const id = setTimeout(() => runLocalSearch(t), 320)
    return () => clearTimeout(id)
  }, [query, tab, runLocalSearch])

  /** 비운영자에게는 질병↔특약 탭을 숨기므로, 해당 탭에 머물러 있으면 검색 탭으로 되돌린다. */
  useEffect(() => {
    if (!isOperator && tab !== TAB_KEYS.CODE) {
      setTab(TAB_KEYS.CODE)
    }
  }, [isOperator, tab])

  const applyChip = (code) => {
    setTab(TAB_KEYS.CODE)
    setSelected(null)
    setQuery(code)
    setRiderQueryCode(code)
  }

  const pickRow = (row) => {
    setSelected(row)
    setRiderQueryCode(row.code || '')
  }

  const tabBtn = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => setTab(key)}
      className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
        tab === key ? 'border-sgez-primary bg-sky-50 text-sgez-primary shadow-sm ring-1 ring-sgez-primary/10' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
      }`}
    >
      {label}
    </button>
  )

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white px-4 py-5 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <span className="text-xl" aria-hidden>📋</span>
              질병코드 · 특약 참고
            </h2>
            {/* <p className="mt-1 text-sm leading-6 text-slate-600">
              KCD 상병코드 검색과 질병–특약 참고 안내를 한 화면에서 확인합니다. 로컬 마스터 기반 검색이며, 특약 매핑은 상품·약관·보험사 기준이 우선입니다.
            </p> */}
          </div>
          <a
            href={KCD_BROWSE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-sm font-medium text-sgez-primary hover:underline"
            title="질병분류정보센터 KOICD"
          >
            KOICD(공식) ↗
          </a>
        </div>

        <div className="mt-5 flex flex-wrap gap-2" role="tablist" aria-label="질병코드 도구 탭">
          {tabBtn(TAB_KEYS.CODE, '질병코드 검색')}
          {isOperator && tabBtn(TAB_KEYS.DISEASE_RIDER, '질병 → 특약')}
          {isOperator && tabBtn(TAB_KEYS.RIDER_DISEASE, '특약 → 질병')}
        </div>
      </section>

      {tab === TAB_KEYS.CODE && (
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3">
            <p className="text-sm text-slate-600">
              질병코드 또는 질병명을 입력하면 상병을 찾습니다. (코드 예: C18 · 명칭 예: 당뇨)
            </p>
          </div>
          <div className="px-4 py-4">
            <label className="relative block">
              <span className="sr-only">검색</span>
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden>🔍</span>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="질병코드(예: I63) 또는 질병명(예: 뇌경색) 검색"
                className="w-full rounded-xl border-2 border-sgez-primary/35 bg-white py-3 pl-10 pr-4 text-sm outline-none ring-0 placeholder:text-slate-400 focus:border-sgez-primary"
              />
            </label>
            {meta && meta.available === false && (
              <p className="mt-3 text-xs text-amber-800">검색 결과가 없습니다. 계속 발생 시 운영자에게 문의해주세요.</p>
            )}
          </div>

          <div className="min-h-[220px] border-t border-slate-100 px-4 py-6">
            {loading && <div className="py-8 text-center text-sm text-slate-500">검색 중…</div>}
            {!loading && String(query).trim().length < 2 && (
              <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
                <span className="text-5xl text-slate-200" aria-hidden>🔍</span>
                <p className="max-w-md text-sm leading-6 text-slate-500">
                  질병코드를 검색해 보세요. 코드번호 또는 질병명으로 검색하면 상병 목록을 확인할 수 있습니다.
                </p>
              </div>
            )}
            {!loading && String(query).trim().length >= 2 && items.length === 0 && (
              <div className="py-10 text-center text-sm text-slate-500">검색 결과가 없습니다.</div>
            )}
            {selected && (
              <div className="mb-4 rounded-lg border border-sgez-primary/25 bg-sgez-background px-3 py-2 text-sm text-slate-800">
                <span className="font-semibold">선택:</span>{' '}
                <span className="font-mono font-bold">{selected.code}</span> {selected.nameKo || ''}
              </div>
            )}
            {!loading && items.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full min-w-[520px] border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-500">
                      <th className="px-3 py-2">코드</th>
                      <th className="px-3 py-2">한글명</th>
                      <th className="px-3 py-2 hidden sm:table-cell">영문명</th>
                      <th className="px-3 py-2 w-[100px]">선택</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((row) => (
                      <tr key={`${row.code}-${row.nameKo}`} className="hover:bg-slate-50/80">
                        <td className="px-3 py-2 font-mono font-semibold text-slate-900">{row.code}</td>
                        <td className="px-3 py-2 text-slate-800">{row.nameKo || '—'}</td>
                        <td className="px-3 py-2 text-slate-500 hidden sm:table-cell">{row.nameEn || '—'}</td>
                        <td className="px-3 py-2">
                          <Button type="button" variant="sgezOutline" size="sm" onClick={() => pickRow(row)}>
                            선택
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 px-4 py-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">11대 중대질환 관련 대표 질병코드</div>
            <p className="mt-1 text-[11px] leading-4 text-slate-500">
              대표적인 11대 질환을 바탕으로 한 코드이빈다. 문진·지표는 보험사·상품마다 다를 수 있습니다.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {MAJOR11_CHIPS.map((ch) => (
                <button
                  key={`m11-${ch.code}`}
                  type="button"
                  onClick={() => applyChip(ch.code)}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-sgez-primary/40 hover:bg-sgez-background"
                >
                  {ch.code} {ch.name}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-100 px-4 py-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">자주 검색하는 질병코드</div>
            <p className="mt-1 text-[11px] leading-4 text-slate-500">통원·일상 상담에서 자주 조회하는 질환 예시입니다.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {FREQUENT_CHIPS.map((ch) => (
                <button
                  key={`fq-${ch.code}`}
                  type="button"
                  onClick={() => applyChip(ch.code)}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:border-sgez-primary/40 hover:bg-sgez-background"
                >
                  {ch.code} {ch.name}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {tab === TAB_KEYS.DISEASE_RIDER && isOperator && (
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3">
            <p className="text-sm text-slate-600">
              질병코드를 입력하면 KCD 대분류 기준으로 특약 담보를 한 번에 짚어볼 때 참고할 수 있는 힌트를 보여 줍니다. 실제 보장 여부는 약관·통원/입원·인수 심사 결과로 확정됩니다.
            </p>
          </div>
          <div className="grid gap-4 px-4 py-4 md:grid-cols-2">
            <label className="block md:col-span-2">
              <span className="text-sm font-semibold text-slate-800">질병코드</span>
              <input
                type="text"
                value={riderQueryCode}
                onChange={(e) => setRiderQueryCode(e.target.value)}
                placeholder="예: C50, I21"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
              />
            </label>
          </div>
          <div className="border-t border-slate-100 px-4 py-4">
            {!guideForSelected ? (
              <p className="text-sm text-slate-500">영문 대문자로 시작하는 KCD 코드를 입력하세요 (예: C50).</p>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-slate-50/90 p-4">
                <div className="text-sm font-bold text-slate-900">
                  {chapterKeyFromCode(riderQueryCode || selected?.code)}장 · {guideForSelected.label}
                </div>
                <div className="mt-1 text-xs text-slate-500">구간 참고: {guideForSelected.block}</div>
                <ul className="mt-3 space-y-1 text-sm leading-6 text-slate-700">
                  {guideForSelected.hints.map((h) => (
                    <li key={h}>- {h}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="border-t border-slate-100 px-4 py-3 text-xs leading-5 text-slate-500">
            사내 DB에 적재된 KCD↔특약 매핑은 운영자 전용 설계지원 화면에서 관리·조회합니다.
          </div>
        </section>
      )}

      {tab === TAB_KEYS.RIDER_DISEASE && isOperator && (
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3">
            <p className="text-sm text-slate-600">
              특약(담보) 유형별로 자주 엮여 검토되는 KCD 구간을 요약했습니다. 실제 청구·지급 기준은 증권 약관과 보험사 기준입니다.
            </p>
          </div>
          <div className="overflow-x-auto px-2 py-4 sm:px-4">
            <table className="w-full min-w-[640px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-500">
                  <th className="px-3 py-2">특약·담보 묶음</th>
                  <th className="px-3 py-2">흔히 연결되는 KCD</th>
                  <th className="px-3 py-2">예시 코드</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {RIDER_TO_DISEASE_ROWS.map((row) => (
                  <tr key={row.category} className="align-top">
                    <td className="px-3 py-3 font-semibold text-slate-900">{row.category}</td>
                    <td className="px-3 py-3 text-slate-600">{row.riders}</td>
                    <td className="px-3 py-3">
                      <div className="text-slate-700">{row.kcd}</div>
                      <div className="mt-1 font-mono text-xs text-slate-500">{row.examples}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  )
}
